import React, { useEffect, useState } from 'react';

/**
 * Public event registration landing — /e/:token
 * No auth. Posts to /api/public/events/:token/register
 */
const EventLanding = () => {
  const token = window.location.pathname.replace(/^\/e\//, '').split(/[/?#]/)[0];
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    intent: 'inquiry',
    consent: false,
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/public/events/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Not found');
        if (!cancelled) setMeta(data);
        const scanKey = `ev_scan_${token}`;
        if (!sessionStorage.getItem(scanKey)) {
          sessionStorage.setItem(scanKey, '1');
          fetch(`/api/public/events/${encodeURIComponent(token)}/scan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ channel: 'qr' }),
          }).catch(() => {});
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/events/${encodeURIComponent(token)}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          channel: 'qr',
          source: 'event_qr',
          landing_page: window.location.href,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const field = {
    display: 'block',
    width: '100%',
    marginTop: 6,
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid #d6d3d1',
    fontSize: '1rem',
    fontFamily: 'inherit',
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(165deg, #fff7ed 0%, #fafaf9 45%, #f5f5f4 100%)',
        fontFamily: '"Tajawal", "Segoe UI", sans-serif',
        padding: '32px 16px 48px',
      }}
    >
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        {meta?.company_logo_url ? (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img
              src={meta.company_logo_url}
              alt={meta.business_name || ''}
              style={{ maxHeight: 180, maxWidth: 360, width: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : (
          <p style={{ textAlign: 'center', fontWeight: 800, color: '#ea580c', letterSpacing: 1, marginBottom: 8 }}>
            {meta?.business_name || 'ريل'}
          </p>
        )}
        {error && !meta && (
          <p style={{ textAlign: 'center', color: '#b91c1c' }}>{error}</p>
        )}
        {meta && (
          <>
            <h1 style={{ textAlign: 'center', fontSize: '1.45rem', color: '#0c0a09', margin: '0 0 6px' }}>
              {meta.name}
            </h1>
            {meta.venue && (
              <p style={{ textAlign: 'center', color: '#78716c', margin: '0 0 20px', fontSize: '0.9rem' }}>
                {meta.venue}
              </p>
            )}
            {done ? (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 28,
                  textAlign: 'center',
                  border: '1px solid #e7e5e4',
                }}
              >
                <p style={{ fontSize: '1.2rem', fontWeight: 700, color: '#059669', margin: 0 }}>تم التسجيل</p>
                <p style={{ color: '#78716c', marginTop: 8, fontSize: '0.9rem' }}>
                  سنتواصل معك قريباً عبر واتساب إن أمكن.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 22,
                  border: '1px solid #e7e5e4',
                  display: 'grid',
                  gap: 14,
                }}
              >
                <label style={{ fontSize: '0.85rem', color: '#44403c', fontWeight: 600 }}>
                  الاسم
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    style={field}
                  />
                </label>
                <label style={{ fontSize: '0.85rem', color: '#44403c', fontWeight: 600 }}>
                  الجوال
                  <input
                    required
                    inputMode="tel"
                    placeholder="05xxxxxxxx"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    style={field}
                  />
                </label>
                <label style={{ fontSize: '0.85rem', color: '#44403c', fontWeight: 600 }}>
                  البريد (اختياري)
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    style={field}
                  />
                </label>
                <label style={{ fontSize: '0.85rem', color: '#44403c', fontWeight: 600 }}>
                  الاهتمام
                  <select
                    value={form.intent}
                    onChange={(e) => setForm({ ...form, intent: e.target.value })}
                    style={field}
                  >
                    <option value="inquiry">استفسار</option>
                    <option value="purchase">شراء</option>
                    <option value="visit">زيارة / معاينة</option>
                    <option value="rent">إيجار</option>
                  </select>
                </label>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.82rem', color: '#57534e' }}>
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                    style={{ marginTop: 3 }}
                  />
                  أوافق على التواصل معي بخصوص هذا الحدث / المشروع
                </label>
                {error && <p style={{ color: '#b91c1c', margin: 0, fontSize: '0.85rem' }}>{error}</p>}
                <button
                  type="submit"
                  disabled={submitting || !form.consent}
                  style={{
                    marginTop: 4,
                    padding: '14px',
                    border: 'none',
                    borderRadius: 12,
                    background: form.consent ? '#ea580c' : '#d6d3d1',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1rem',
                    cursor: form.consent ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}
                >
                  {submitting ? '…' : 'تسجيل الاهتمام'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventLanding;
